import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Container, PageHeader, Modal } from '../layout';
import { Button } from '../layout/buttons';
import { Col, Row } from 'react-bootstrap';
import CanvassInfo from './CanvassInfo';
import { AugAlert } from '../layout/alert/AugAlert';
import Icon from '../layout/icons/Icons';

import styles from './Canvass.module.css';
import { fetchCanvassById, saveCanvass } from '../../utils/canvasses';
import { generateStrategyOutline } from '../../utils/discovery';

const buildExtractedInsights = (canvass) => {
  if (!canvass) return null;
  const info = [];

  // General info
  if (canvass.canvassName) info.push({ key: 'canvass_name', value: canvass.canvassName, tags: ['general_info'] });
  if (canvass.canvassDescription) info.push({ key: 'canvass_description', value: canvass.canvassDescription, tags: ['general_info'] });
  if (canvass.environment) info.push({ key: 'environment', value: canvass.environment, tags: ['general_info'] });
  if (typeof canvass.success_metrics === 'string' && canvass.success_metrics.trim()) {
    info.push({ key: 'success_metrics', value: canvass.success_metrics, tags: ['general_info'] });
  }

  // Audience profile
  if (canvass.audience) info.push({ key: 'audience', value: canvass.audience, tags: ['audience_profile'] });
  if (Array.isArray(canvass.touchpoints) && canvass.touchpoints.length) {
    info.push({ key: 'touchpoints', value: canvass.touchpoints, tags: ['audience_profile'] });
  }

  // Brand / introductions
  if (Array.isArray(canvass.interview_introductions)) {
    // Ensure one introduction is selected by default
    const introductions = [...canvass.interview_introductions];
    if (!introductions.some(intro => intro && intro.selected === true) && introductions.length > 0) {
      introductions[0] = { ...(introductions[0] || {}), selected: true };
    }

    introductions.forEach((intro, idx) => {
      if (intro.badge_theme) info.push({ key: `intro_${idx}_badge_theme`, value: intro.badge_theme, tags: ['brand_context'] });
      if (intro.introduction_text) info.push({ key: `intro_${idx}_introduction_text`, value: intro.introduction_text, tags: ['brand_context'] });
      if (Array.isArray(intro.sample_badges) && intro.sample_badges.length) {
        const badges = intro.sample_badges.map(b => (b.badge || b.name) + (b.description ? `: ${b.description}` : '')).join(', ');
        info.push({ key: `intro_${idx}_sample_badges`, value: badges, tags: ['brand_context'] });
      }
    });
    // Use the adjusted introductions downstream
    canvass.interview_introductions = introductions;
  }

  // Research objective (string)
  if (typeof canvass.objective === 'string' && canvass.objective.trim()) {
    info.push({ key: 'objective', value: canvass.objective, tags: ['research_objective'] });
  }

  // Constraints (ensure presence for UI consumers)
  if (!canvass.constraints) {
    canvass.constraints = { tone: '', timebox: '', incentive: '' };
  }

  // Sub-objectives (string[])
  if (Array.isArray(canvass.sub_objectives)) {
    canvass.sub_objectives.forEach((so, i) => {
      if (typeof so === 'string' && so.trim()) {
        info.push({ key: `sub_${i}_title`, value: so, tags: ['sub_objective'] });
      }
    });
  }

  // Context fields
  const behaviouralContext = typeof canvass.behavioural_context === 'string' && canvass.behavioural_context.trim()
    ? canvass.behavioural_context
    : (typeof canvass.extracted_info?.behavioural_context === 'string' && canvass.extracted_info.behavioural_context.trim()
      ? canvass.extracted_info.behavioural_context
      : '');
  if (behaviouralContext) {
    info.push({ key: 'behavioural_context', value: behaviouralContext, tags: ['context'] });
  }
  if (typeof canvass.observed_behaviors === 'string' && canvass.observed_behaviors.trim()) {
    info.push({ key: 'observed_behaviors', value: canvass.observed_behaviors, tags: ['context'] });
  }
  if (typeof canvass.external_context === 'string' && canvass.external_context.trim()) {
    info.push({ key: 'external_context', value: canvass.external_context, tags: ['context'] });
  }

  const initial = { extractedInfo: info, extractedAt: new Date().toLocaleString() };
  if (Array.isArray(canvass.interview_introductions)) {
    initial.interview_introductions = canvass.interview_introductions;
  }
  if (canvass.interviewLink) {
    initial.interviewLink = canvass.interviewLink;
  }
  return initial;
};

const Canvass = () => {
  const navigate = useNavigate();
  const { canvassId } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [canvass, setCanvass] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showEditTitle, setShowEditTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [generatingStrategy, setGeneratingStrategy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!canvassId) return;
      try {
        setLoading(true);
        setError('');
        const result = await fetchCanvassById({ canvassId });
        if (cancelled) return;
        if(!('environment' in result)) {
          result.environment = '';
        }
        setCanvass(result);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load canvass');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [canvassId]);

  // Handle saving arbitrary canvass field patches from child components
  const handleSaveCanvassPatch = async (patch) => {
    if (!canvass || !patch || typeof patch !== 'object') return;
    try {
      const outgoing = { ...canvass, ...patch };
      const saved = await saveCanvass(outgoing);
      setCanvass(saved);
    } catch (e) {
      setError(e?.message || 'Failed to save canvass');
      throw e;
    }
  };

  const initialExtracted = useMemo(() => buildExtractedInsights(canvass), [canvass]);

  // Handle saving interview introductions from child
  const handleSaveIntroductions = async (updatedIntroductions) => {
    if (!canvass) return;
    try {
      const outgoing = { ...canvass, interview_introductions: updatedIntroductions };
      const saved = await saveCanvass(outgoing);
      setCanvass(saved);
    } catch (e) {
      setError(e?.message || 'Failed to save canvass');
      throw e;
    }
  };

  const handleSaveTitle = async () => {
    if (!canvass) return;
    const nextTitle = String(editedTitle || '').trim();
    if (!nextTitle) {
      setError('Title cannot be empty');
      return;
    }
    try {
      setTitleSaving(true);
      setError('');
      const outgoing = { ...canvass, canvassName: nextTitle };
      const saved = await saveCanvass(outgoing);
      setCanvass(saved);
      setShowEditTitle(false);
    } catch (e) {
      setError(e?.message || 'Failed to save canvass');
      throw e;
    } finally {
      setTitleSaving(false);
    }
  };

  const handleGenerateStrategyOutline = async () => {
    if (!canvass || !canvassId) return;
    
    try {
      setGeneratingStrategy(true);
      setError('');
      
      // Generate the strategy outline
      const strategyOutline = await generateStrategyOutline({ canvassId });
      
      // Refresh the canvass data to get the updated strategy outline
      const updatedCanvass = await fetchCanvassById({ canvassId });
      setCanvass(updatedCanvass);
      
    } catch (e) {
      setError('Failed generating essential canvass information');
    } finally {
      setGeneratingStrategy(false);
    }
  };

  const title = canvass?.canvassName || canvassId || 'Canvass';
  const description = canvass?.canvassDescription || 'Review and edit your canvass details.';

  // Compute relative "last updated" text from canvass.timestamp
  const lastUpdatedText = useMemo(() => {
    if (!canvass?.timestamp) return '';
    const ts = new Date(canvass.timestamp).getTime();
    if (Number.isNaN(ts)) return '';
    const diffMs = Date.now() - ts;
    const sec = Math.max(0, Math.floor(diffMs / 1000));
    if (sec < 60) return `Last updated ${sec} second${sec === 1 ? '' : 's'} ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `Last updated ${min} minute${min === 1 ? '' : 's'} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `Last updated ${hr} hour${hr === 1 ? '' : 's'} ago`;
    const day = Math.floor(hr / 24);
    return `Last updated ${day} Day${day === 1 ? '' : 's'} ago`;
  }, [canvass?.timestamp]);

  return (
    <Container fluid>
        <Row>
            <Col className="d-flex justify-content-start">
                <a className={styles.backLink} href="/canvasses" aria-label="Back to Canvasses">
                    <Icon className={styles.backIcon} name="arrows/arrow_back" color="var(--brand-as-blue-600)" width={22} height={22} />
                    <div className={styles.backText}>Back to Canvasses</div>
                </a>
            </Col>
        </Row>
        <Row className="align-items-center">
            <Col md={8}>
                <PageHeader
                  title={
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span>{title}</span>
                      {canvass?.displayName ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-as-neutral-700, #4b5563)',
                            background: 'var(--brand-as-neutral-black-transparent-05)'
                          }}
                          title={ canvass.displayName }
                        >
                          { canvass.displayName }
                        </span>
                      ) : null}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={() => { setEditedTitle(canvass?.canvassName || ''); setShowEditTitle(true); }}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setEditedTitle(canvass?.canvassName || ''); setShowEditTitle(true); } }}
                        aria-label="Edit canvass title"
                        title="Edit title"
                        style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}
                      >
                        <Icon name="user_actions/edit" color="var(--brand-as-purple-600)" width={18} height={18} />
                      </span>
                    </span>
                  }
                  subtext={description}
                  align="left"
                  size="lg"
                  className="text-primary"
                />
            </Col>
            <Col md={4} className="d-flex justify-content-end">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {(canvass?.companyContext || canvass?.stakeholderContext) && (
                        <span
                          className="badge bg-secondary text-dark"
                          style={{ marginRight: "12px" }}
                          title={
                            canvass?.companyContext 
                              ? `Company: ${canvass.companyContext.name || "Selected Company"}`
                              : canvass?.stakeholderContext
                              ? `Stakeholder: ${canvass.stakeholderContext.name || "Selected Stakeholder"}`
                              : "Context"
                          }
                        >
                          {canvass?.companyContext 
                            ? `Company: ${canvass.companyContext.name || "Selected Company"}`
                            : canvass?.stakeholderContext
                            ? `Stakeholder: ${canvass.stakeholderContext.name || "Selected Stakeholder"}`
                            : "Context"
                          }
                        </span>
                    )}
                    {lastUpdatedText && (
                        <span style={{ color: 'var(--brand-as-neutral-700, #4b5563)', fontSize: 12 }}>{lastUpdatedText}</span>
                    )}
                    {canvass?.status === 'approved' ? (
                        <Button textColor="var(--system-sky-600)" color="var(--system-sky-050)" text="PENDING ACTIVATION">PENDING ACTIVATION</Button>
                    ) : canvass?.status === 'active' ? (
                        <Button textColor="#fff" color="var(--brand-as-green-600)" text="LIVE">LIVE</Button>
                    ) : canvass?.status === 'complete' ? (
                        <Button textColor="#fff" color="var(--brand-as-green-600)" text="COMPLETE">COMPLETE</Button>
                    ) : (
                        <Button
                          textColor="var(--brand-as-neutral-black-950)"
                          color="var(--brand-as-neutral-black-transparent-05)"
                          text="Finish Later"
                          onClick={() => navigate('/canvasses')}
                        >
                          Finish Later
                        </Button>
                    )}
                </div>
            </Col>
        </Row>

        {showEditTitle && (
          <Modal
            open={showEditTitle}
            onClose={() => { if (!titleSaving) { setShowEditTitle(false); } }}
            title="Edit Canvass Title"
            footer={
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button
                  textColor="var(--brand-as-neutral-black-950)"
                  color="var(--brand-as-neutral-black-transparent-05)"
                  onClick={() => setShowEditTitle(false)}
                  disabled={titleSaving}
                >Cancel</Button>
                <Button
                  textColor="#fff"
                  color="var(--brand-as-blue-600)"
                  onClick={handleSaveTitle}
                  disabled={titleSaving || !String(editedTitle || '').trim()}
                >{titleSaving ? 'Saving...' : 'Save'}</Button>
              </div>
            }
          >
            <div style={{ display: 'grid', gap: 8 }}>
              <label htmlFor="canvass-title" style={{ fontWeight: 600, textAlign: 'left', justifySelf: 'start' }}>Canvass Title</label>
              <input
                id="canvass-title"
                type="text"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleSaveTitle(); } }}
                disabled={titleSaving}
                style={{
                  border: '1px solid var(--brand-as-neutral-black-transparent-10)',
                  borderRadius: 8,
                  padding: '10px 12px',
                }}
              />
            </div>
          </Modal>
        )}

      {error && (
        <div style={{ marginBottom: 12, marginTop: 12 }}>
          <AugAlert type="error" title="Error" text={error} dismissible onDismiss={() => setError('')}/>
        </div>
      )}

      {loading && <div className="text-muted mb-3">Loading canvass…</div>}
      
      {canvass?.status === 'draft' && (!canvass?.strategyOutline || canvass?.interview_introductions?.length === 0) && (() => {
        const canvassTimestamp = new Date(canvass?.timestamp || canvass?.createdAt || Date.now());
        const timeDiff = Date.now() - canvassTimestamp.getTime();
        const isRecent = timeDiff < 5 * 60 * 1000; // 5 minutes

        if (isRecent || canvass?.interview_introductions?.length === 0) {
          return (
            <div style={{ marginBottom: 12, marginTop: 12 }}>
              <AugAlert
                type="info"
                title="Setting up your canvass"
                text="We're setting up some essential information in the background. Please check back in a couple of minutes."
                dismissible={false}
              />
            </div>
          );
        }

        return (
          <div style={{ marginBottom: 12, marginTop: 12 }}>
            <AugAlert
              type="warning"
              title="Canvass Essential Information Missing"
              text="This canvass is in draft status but missing key information. Generate the essential information to complete the canvass setup."
              link={{
                label: generatingStrategy ? 'Generating...' : 'Generate Essential Information',
                onClick: handleGenerateStrategyOutline,
                disabled: generatingStrategy
              }}
              dismissible={false}
            />
          </div>
        );
      })()}
      
      {canvass?.status === 'approved' && (
        <div style={{ marginBottom: 12, marginTop: 12 }}>
          <AugAlert
            type="discovery"
            title="Pending activation"
            text="This canvass is ready and waiting for activation. You can preview and test the interview, but no data will be collected until its activated"
          />
        </div>
      )}

      {activeTab === 'results' && (['active', 'complete'].includes(String(canvass?.status || ''))) && (
        <div style={{ marginBottom: 12, marginTop: 12 }}>
          <AugAlert
              type="discovery"
              title="Results in Beta"
              text="The Results tab is currently in beta and improving every day."
          />
        </div>
      )}

      <CanvassInfo
        initialExtractedInsights={initialExtracted}
        onSaveIntroductions={handleSaveIntroductions}
        onSaveCanvassPatch={handleSaveCanvassPatch}
        canvass={canvass}
        onActiveTabChange={setActiveTab}
      />
    </Container>
  );
};

export default Canvass;






